import React from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../../components/ui/DataTable";
import { TableStyles } from "@/components/ui";
import { Eye, Edit2, Calculator, Download } from "lucide-react";
import toast from "react-hot-toast";
import api, { API_BASE_URL } from "../../../services/api";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const CustomersTableView = ({
  filtered,
  onCustomerClick,
  stages,
  onEdit,
  onToggleDisable,
  isLoading = false,
  onUpdateNextStage,
}) => {
  const navigate = useNavigate();

  const handleDownloadPdf = async (lead) => {
    const customerId = lead._id || lead.id;
    try {
      toast.loading("Fetching PDF...", { id: "pdf-download" });
      const res = await api.get(`/estimations/customer/${customerId}`);
      if (res.data?.success && res.data?.data?._id) {
        const estId = res.data.data._id;
        if (!res.data.data.google_drive_file_id) {
          toast.error("No PDF found in Drive for this customer", { id: "pdf-download" });
          return;
        }
        window.open(`${API_BASE_URL}/api/estimations/${estId}/stream-pdf?download=true`, "_blank");
        toast.success("Download started", { id: "pdf-download" });
      } else {
        toast.error("No estimation found for this customer", { id: "pdf-download" });
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to download PDF", { id: "pdf-download" });
    }
  };

  const tableColumns = [
    {
      header: "SD ID",
      minWidth: "80px",
      accessor: (l) => (
        <span className={TableStyles.idCell}>
          {l.sdId || l.sd_id || l.customId || "—"}
        </span>
      ),
    },
    {
      header: "Created Date",
      minWidth: "100px",
      accessor: (l) => (
        <span className={TableStyles.regularText}>
          {formatDate(l.createdAt)}
        </span>
      ),
    },
    {
      header: "Customer Name",
      minWidth: "160px",
      accessor: (l) => {
        const fullName =
          l.customerName ||
          `${l.firstName || ""} ${l.lastName || ""}`.trim() ||
          "Unknown";
        return (
          <div className="flex flex-col min-w-0">
            <span className={TableStyles.primaryText}>{fullName}</span>
          </div>
        );
      },
    },
    {
      header: "Contact",
      minWidth: "120px",
      accessor: (l) => {
        const phone = l.phoneNumber || l.phone || "—";
        const email = l.email || l.customerEmail;
        const hasEmail = email && email !== "No Email";
        return (
          <div className="flex flex-col gap-0.5">
            <span className={TableStyles.primaryText}>{phone}</span>
            {hasEmail && (
              <span
                className={`${TableStyles.secondaryText} truncate max-w-[170px] block`}
                title={email}
              >
                {email}
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: "Customer Type",
      minWidth: "110px",
      accessor: (l) => (
        <span className={TableStyles.regularText}>
          {l.salesType || l.customerType || "—"}
        </span>
      ),
    },
    {
      header: "Lead Source",
      minWidth: "100px",
      accessor: (l) => (
        <span className={TableStyles.regularText}>
          {l.referenceType || l.source || "Walk-In"}
        </span>
      ),
    },
    {
      header: "Assigned To",
      minWidth: "110px",
      accessor: (l) => {
        let staffName = "Unassigned";
        if (typeof l.assignedTo === "object" && l.assignedTo !== null) {
          staffName = l.assignedTo.name || 
                      (l.assignedTo.firstName ? `${l.assignedTo.firstName} ${l.assignedTo.lastName || ''}`.trim() : null) || 
                      l.assignedTo.username || 
                      "Unassigned";
        } else if (typeof l.assignedTo === "string" && l.assignedTo.trim() !== '') {
          staffName = l.assignedTo;
        } else if (l.telecaller?.name) {
          staffName = l.telecaller.name;
        }
        
        const displayValue = l.assignedTeam || (staffName !== "Unassigned" ? staffName : "Unassigned");
        return (
          <span className={TableStyles.regularText}>{displayValue}</span>
        );
      },
    },
    {
      header: "Interested In",
      minWidth: "140px",
      accessor: (l) => (
        <span className={TableStyles.regularText}>
          {l.interestedIn || l.projectType || l.requirement || "—"}
        </span>
      ),
    },
    {
      header: "Category",
      minWidth: "90px",
      accessor: (l) => {
        const cat = l.category || "Enquiry";
        const style =
          cat === "Enquiry"
            ? "bg-amber-50 text-amber-700 border-amber-200"
            : "bg-slate-50 text-slate-600 border-slate-200";
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] border capitalize font-normal ${style}`}
          >
            {cat}
          </span>
        );
      },
    },

    {
      header: "Last Status Date",
      minWidth: "110px",
      accessor: (l) => (
        <span className={TableStyles.regularText}>
          {formatDate(l.updatedAt || l.createdAt)}
        </span>
      ),
    },
    {
      header: "Status",
      minWidth: "100px",
      accessor: (l) => {
        const displayStatus =
          l.status === "drop" ? "drop" : l.teleStatus || l.status;
        const stage = stages?.find((s) => s.key === displayStatus);
        const style = stage?.color
          ? `bg-${stage.color}-50 text-${stage.color}-700 border-${stage.color}-200`
          : "bg-emerald-50 text-emerald-700 border-emerald-200";
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] border capitalize font-normal ${style}`}
          >
            {stage?.label || displayStatus || "New"}
          </span>
        );
      },
    },
    {
      header: "Next Stage",
      minWidth: "130px",
      accessor: (l) => {
        const nextStageOptions = [
          "Estimation Shared",
          "Order Confirmed",
          "Pending Approval",
          "Approved",
          "In Store",
          "Dispatched",
          "Completed",
        ];
        return (
          <select
            value={l.status || l.next_stage || ""}
            onChange={async (e) => {
              const val = e.target.value;
              if (val) {
                try {
                  await onUpdateNextStage(l._id || l.id, val);
                } catch (err) {
                  console.error(err);
                }
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className="px-2 py-1 text-[11px] font-normal border border-slate-200 rounded bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-900 cursor-pointer shadow-sm"
          >
            <option value="">Next Stage</option>
            {nextStageOptions.map((opt, idx) => (
              <option key={idx} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      header: "Store",
      minWidth: "100px",
      accessor: (l) => {
        const storeName = l.store?.name || "—";
        return (
          <span className={`${TableStyles.regularText} truncate max-w-[120px] block`} title={storeName}>
            {storeName}
          </span>
        );
      },
    },
    {
      header: "Actions",
      minWidth: "120px",
      accessor: (l) => (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onCustomerClick && onCustomerClick(l); }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all focus:outline-none"
            title="View Details"
          >
            <Eye size={15} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit && onEdit(l); }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all focus:outline-none"
            title="Edit Customer"
          >
            <Edit2 size={15} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/workspace/${l._id || l.id || "1"}`); }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-[#C91D25] hover:bg-red-50 transition-all focus:outline-none"
            title="Go to Workspace"
          >
            <Calculator size={15} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDownloadPdf(l); }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all focus:outline-none"
            title="Download PDF"
          >
            <Download size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full bg-transparent relative z-10 animate-in fade-in duration-500">
      <DataTable
        columns={tableColumns}
        data={filtered}
        isLoading={isLoading}
        onRowClick={null}
      />
    </div>
  );
};
